import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/** @jsx jsx */


import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { jsx } from '@emotion/core';
import rafSchd from 'raf-schd';
import { COLLAPSED_LEFT_SIDEBAR_WIDTH, DEFAULT_LEFT_SIDEBAR_WIDTH, IS_SIDEBAR_DRAGGING, MIN_LEFT_SIDEBAR_DRAG_THRESHOLD, RESIZE_BUTTON_SELECTOR, RESIZE_CONTROL_SELECTOR, VAR_LEFT_SIDEBAR_WIDTH } from '../../common/constants';
import { getLeftPanelWidth, getLeftSidebarPercentage } from '../../common/utils';
import { SidebarResizeContext } from '../../controllers/sidebar-resize-context';
/* import useUpdateCssVar from '../../controllers/use-update-css-vars'; */

import GrabArea from './grab-area';
import ResizeButton from './resize-button';
import { resizeControlCSS, shadowCSS } from './styles';

var cssSelector = _defineProperty({}, RESIZE_CONTROL_SELECTOR, true);

var Shadow = function Shadow(_ref) {
  var testId = _ref.testId;
  return jsx("div", {
    "data-testid": testId,
    css: shadowCSS
  });
};

var ResizeControl = function ResizeControl(_ref2) {
  var testId = _ref2.testId,
      overrides = _ref2.overrides,
      _ref2$resizeButtonLab = _ref2.resizeButtonLabel,
      resizeButtonLabel = _ref2$resizeButtonLab === void 0 ? '' : _ref2$resizeButtonLab,
      _ref2$resizeGrabAreaL = _ref2.resizeGrabAreaLabel,
      resizeGrabAreaLabel = _ref2$resizeGrabAreaL === void 0 ? 'Resize' : _ref2$resizeGrabAreaL,
      onResizeStart = _ref2.onResizeStart,
      onResizeEnd = _ref2.onResizeEnd;

  var _useContext = useContext(SidebarResizeContext),
      expandLeftSidebar = _useContext.expandLeftSidebar,
      collapseLeftSidebar = _useContext.collapseLeftSidebar,
      leftSidebarState = _useContext.leftSidebarState,
      setLeftSidebarState = _useContext.setLeftSidebarState;

  var isLeftSidebarCollapsed = leftSidebarState.isLeftSidebarCollapsed,
      isResizing = leftSidebarState.isResizing;
  var x = useRef(leftSidebarState[VAR_LEFT_SIDEBAR_WIDTH]); // Distance of mouse from left sidebar onMouseDown

  var offset = useRef(0);
  var keyboardEventTimeout = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isGrabAreaFocused = _useState2[0],
      setIsGrabAreaFocused = _useState2[1];

  var toggleSideBar = function toggleSideBar(e) {
    if (isResizing) {
      return;
    }

    if (isLeftSidebarCollapsed) {
      expandLeftSidebar();
    } else {
      collapseLeftSidebar();
    } // Bring focus to the resize button if the grab area is
    // "clicked" using enter/space on keyboard.


    if (e && e.nativeEvent.detail === 0) {
      var _resizeButton = document.querySelector("[".concat(RESIZE_BUTTON_SELECTOR, "]"));

      _resizeButton && _resizeButton.focus();
    }
  };

  var onMouseDown = function onMouseDown(event) {
    if (isLeftSidebarCollapsed) {
      return;
    }

    offset.current = event.clientX - leftSidebarState[VAR_LEFT_SIDEBAR_WIDTH] - getLeftPanelWidth();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.documentElement.setAttribute(IS_SIDEBAR_DRAGGING, 'true');

    var newLeftbarState = _objectSpread(_objectSpread({}, leftSidebarState), {}, {
      isResizing: true
    });

    setLeftSidebarState(newLeftbarState);
    onResizeStart && onResizeStart(newLeftbarState);
  };

  var cancelDrag = function cancelDrag(shouldCollapse) {
    onMouseMove.cancel();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.documentElement.removeAttribute(IS_SIDEBAR_DRAGGING);
    offset.current = 0;
    collapseLeftSidebar(undefined, true);
  };

  var onMouseMove = rafSchd(function (event) {
    // Allow the sidebar to be 50% of the available page width
    var maxWidth = Math.round(window.innerWidth / 2);
    var leftPanelWidth = getLeftPanelWidth();
    var leftSidebarWidth = leftSidebarState.leftSidebarWidth;
    var invalidDrag = event.clientX < 0;

    if (invalidDrag) {
      cancelDrag();
    }

    var delta = Math.max(Math.min(event.clientX - leftSidebarWidth - leftPanelWidth, maxWidth - leftSidebarWidth - leftPanelWidth), COLLAPSED_LEFT_SIDEBAR_WIDTH - leftSidebarWidth - leftPanelWidth);
    x.current = Math.max(leftSidebarWidth + delta - offset.current, COLLAPSED_LEFT_SIDEBAR_WIDTH);
    document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(x.current, "px"));
  });

  var cleanupAfterResize = function cleanupAfterResize() {
    x.current = 0;
    offset.current = 0;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  var updatedLeftSidebarState = {};

  var onMouseUp = function onMouseUp(event) {
    if (isLeftSidebarCollapsed) {
      return;
    }

    document.documentElement.removeAttribute(IS_SIDEBAR_DRAGGING); // If it is dragged to below the threshold,
    // collapse the navigation

    if (x.current < MIN_LEFT_SIDEBAR_DRAG_THRESHOLD) {
      document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(COLLAPSED_LEFT_SIDEBAR_WIDTH, "px"));
      collapseLeftSidebar(undefined, true);
    } // If it is dragged to position in between the
    // min threshold and default width
    // expand the nav to the default width
    else if (x.current > MIN_LEFT_SIDEBAR_DRAG_THRESHOLD && x.current < DEFAULT_LEFT_SIDEBAR_WIDTH) {
      var _objectSpread2;

      document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(DEFAULT_LEFT_SIDEBAR_WIDTH, "px"));
      updatedLeftSidebarState = _objectSpread(_objectSpread({}, leftSidebarState), {}, (_objectSpread2 = {
        isResizing: false
      }, _defineProperty(_objectSpread2, VAR_LEFT_SIDEBAR_WIDTH, DEFAULT_LEFT_SIDEBAR_WIDTH), _defineProperty(_objectSpread2, "lastLeftSidebarWidth", DEFAULT_LEFT_SIDEBAR_WIDTH), _objectSpread2));
      setLeftSidebarState(updatedLeftSidebarState);
    } else {
      var _objectSpread3; // otherwise resize it to the desired width


      updatedLeftSidebarState = _objectSpread(_objectSpread({}, leftSidebarState), {}, (_objectSpread3 = {
        isResizing: false
      }, _defineProperty(_objectSpread3, VAR_LEFT_SIDEBAR_WIDTH, x.current), _defineProperty(_objectSpread3, "lastLeftSidebarWidth", x.current), _objectSpread3));
      setLeftSidebarState(updatedLeftSidebarState);
    }

    requestAnimationFrame(function () {
      onMouseMove.cancel();
      setIsGrabAreaFocused(false);
      onResizeEnd && onResizeEnd(updatedLeftSidebarState);
      cleanupAfterResize();
    });
  };

  var onKeyDown = function onKeyDown(event) {
    if (isLeftSidebarCollapsed || !isGrabAreaFocused) {
      return false;
    }

    var key = event.key;
    var isLeftOrTopArrow = key === 'ArrowLeft' || key === 'ArrowUp' || key === 'Left' || key === 'Up';
    var isRightOrBottomArrow = key === 'ArrowRight' || key === 'ArrowDown' || key === 'Right' || key === 'Down';
    var isSpaceOrEnter = key === 'Enter' || key === 'Spacebar' || key === ' ';

    if (isSpaceOrEnter) {
      toggleSideBar(event);
      event.preventDefault();
    }

    if (isLeftOrTopArrow || isRightOrBottomArrow) {
      event.preventDefault(); // prevent content scroll

      onResizeStart && onResizeStart(leftSidebarState);
      var step = 10;
      var stepValue = isLeftOrTopArrow ? -step : step;
      var leftSidebarWidth = leftSidebarState.leftSidebarWidth;
      var maxWidth = Math.round(window.innerWidth / 2) - getLeftPanelWidth();
      var hasModifierKey = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
      var width = leftSidebarWidth + stepValue;

      if (width <= DEFAULT_LEFT_SIDEBAR_WIDTH) {
        width = DEFAULT_LEFT_SIDEBAR_WIDTH;
        document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(DEFAULT_LEFT_SIDEBAR_WIDTH - 20, "px"));
      } else if (width > maxWidth) {
        width = maxWidth;
        document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(maxWidth + 20, "px"));
      } else if (hasModifierKey) {
        width = isRightOrBottomArrow ? maxWidth : DEFAULT_LEFT_SIDEBAR_WIDTH;
      } // Nesting the setTimeout within requestAnimationFrame helps
      // the browser schedule the setTimeout call in an efficient manner


      requestAnimationFrame(function () {
        keyboardEventTimeout.current = window.setTimeout(function () {
          var _objectSpread4;

          keyboardEventTimeout.current && clearTimeout(keyboardEventTimeout.current);
          document.documentElement.style.setProperty("--".concat(VAR_LEFT_SIDEBAR_WIDTH), "".concat(width, "px"));

          var updatedLeftSidebarState = _objectSpread(_objectSpread({}, leftSidebarState), {}, (_objectSpread4 = {}, _defineProperty(_objectSpread4, VAR_LEFT_SIDEBAR_WIDTH, width), _defineProperty(_objectSpread4, "lastLeftSidebarWidth", width), _objectSpread4));

          setLeftSidebarState(updatedLeftSidebarState);
          onResizeEnd && onResizeEnd(updatedLeftSidebarState);
        }, 50);
      });
    }
  };

  var onFocus = useCallback(function () {
    setIsGrabAreaFocused(true);
  }, []);
  var onBlur = useCallback(function () {
    setIsGrabAreaFocused(false);
  }, []);

  var resizeButton = _objectSpread({
    render: function render(Component, props) {
      return jsx(Component, props);
    }
  }, overrides && overrides.ResizeButton); // This width is calculated once only on mount.
  // This means resizing the window will cause this value to be incorrect for screen reader users,
  // however this comes with a substantial performance gain and so is considered acceptable.


  var maxAriaWidth = useMemo(function () {
    var innerWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
    return Math.round(innerWidth / 2) - getLeftPanelWidth();
  }, []);
  var leftSidebarPercentageExpanded = getLeftSidebarPercentage(leftSidebarState.leftSidebarWidth, maxAriaWidth);
  /* eslint-disable jsx-a11y/role-supports-aria-props */

  return jsx("div", _extends({}, cssSelector, {
    css: resizeControlCSS(isGrabAreaFocused, isLeftSidebarCollapsed)
  }), jsx(Shadow, {
    testId: testId && "".concat(testId, "-shadow")
  }), jsx(GrabArea, {
    role: "separator",
    "aria-label": resizeGrabAreaLabel,
    "aria-valuenow": leftSidebarPercentageExpanded,
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    "aria-expanded": !isLeftSidebarCollapsed,
    onKeyDown: onKeyDown,
    onMouseDown: onMouseDown,
    onFocus: onFocus,
    onBlur: onBlur,
    testId: testId && "".concat(testId, "-grab-area"),
    isLeftSidebarCollapsed: isLeftSidebarCollapsed,
    disabled: isLeftSidebarCollapsed
  }), resizeButton.render(ResizeButton, {
    isLeftSidebarCollapsed: isLeftSidebarCollapsed,
    label: resizeButtonLabel,
    onClick: toggleSideBar,
    testId: testId && "".concat(testId, "-resize-button")
  }));
  /* eslint-enable jsx-a11y/role-supports-aria-props */
};

export default ResizeControl;